import React, { useCallback, useMemo, useState, useEffect } from 'react';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { INDEX_FIELDS, METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange, getAppName, safeDivide } from 'src/utils/app';
import { useAppSelector } from 'src/utils/Hooks';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import EntityTableHeader, { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import ProductGrid, { ProductGridDatum } from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import _get from 'lodash/get';
import { shouldHidePagination } from 'src/components/BeaconV2Components/EntityTableRefresh/utils';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { Box } from '@mui/material';
import { useMainEntityId, useMainEntityType, useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import { getWeekColumnDefinition } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { useDorisTableData, DorisTableDataRecord } from 'src/serverProxy/useDorisTableData';
import {
  DorisGroupByFieldNameEnum,
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import useProductMetaData from 'src/components/AtlasV2Components/Hooks/useProductMetaData';
import AtlasSalesExportModal from 'src/components/AtlasV2Components/AtlasDorisExportComponents/AtlasSalesExportModal';
import { getAtlasSalesV2Treatment } from 'src/utils/app';

// Interface for sales data records that extends the base DorisTableDataRecord
interface DorisSalesDataRecord extends DorisTableDataRecord {
  retailSales: number;
  unitsSold: number;
  retailPrice: number;
  retailSalesComparison?: number;
  unitsSoldComparison?: number;
  retailPriceComparison?: number;
}

const PAGE_SIZE = 20;
const FETCH_CHUNK_SIZE = 100;

const PAGES_PER_CHUNK = Math.ceil(FETCH_CHUNK_SIZE / PAGE_SIZE);
const PRODUCT_API_METRIC_TYPE_BY_WEEK = 'gettrafficsummarybyweek';
const PRODUCT_API_METRIC_TYPE_BY_SKU = 'GetTrafficCountSummaryByType';

const METRICS = [
  ['purchaseRate', 'sales'],
  ['retailSales', 'sales'],
  ['unitsSold', 'sales'],
  ['retailPrice', 'sales']
];

const TableNoDataPlaceHolder = (
  <NoDataPlaceHolder
    title="Data currently unavailable"
    description="There is currently no data matching your selected criteria. Please try another query to view insights."
    flexProps={{
      paddingTop: '12.5px',
      paddingBottom: '12.5px'
    }}
  />
);

/**
 * Conversion rate grid in the traffic conversion page
 */
export default function AtlasV2ConversionV2ProductGrid() {
  const entityType = useMainEntityType();
  const isProductEntity = entityType === 'product';
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'tile' | 'table'>(isProductEntity ? 'table' : 'tile');
  const [dataByPageChunk, setDataByPageChunk] = useState<{ [key: number]: any[] }>({});

  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const mainEntityId = useMainEntityId();
  const retailerId = useRetailerId();

  // Reset page when time periods change
  useEffect(() => {
    setPage(1);
    setDataByPageChunk({});
  }, [startWeek, endWeek, comparisonStartWeek, comparisonEndWeek]);

  /**
   * Returns the chunk number we are using to fetch. For example, if we show 20 items
   * per page and fetch 100 items, then page 1-5 will be chunk 1, page 6-10 will be chunk 2, etc.
   */
  const computeChunkPage = useCallback((displayedPage: number) => {
    return Math.ceil(displayedPage / PAGES_PER_CHUNK);
  }, []);

  const currentChunk = computeChunkPage(page);

  const { rawResponse: salesDorisResponse, isLoading: salesDorisLoading } = useDorisTableData({
    apiMethodName: DorisMetricEndPointEnum.SalesEndPoint,
    groupByFieldName: isProductEntity ? DorisGroupByFieldNameEnum.Week : DorisGroupByFieldNameEnum.Product,
    metricType: DorisMetricTypeEnum.ProductsWithSalesData
  });

  /**
   * Returned SKUs so that we can query traffic data for each SKU
   */
  const skus = isProductEntity ? [] : salesDorisResponse?.primaryData?.records?.map((row) => row.fieldKey);

  const { rawResponse: trafficDorisResponse, isLoading: trafficDorisLoading } = useDorisTableData({
    apiMethodName: isProductEntity ? PRODUCT_API_METRIC_TYPE_BY_WEEK : PRODUCT_API_METRIC_TYPE_BY_SKU,
    groupByFieldName: 'product',
    metricType: 'totalClicks',
    additionalParams: {
      stacklineSkus: isProductEntity ? [mainEntityId] : skus
    },
    shouldPerformFetch: !salesDorisLoading
  });

  // Fetch product metadata for the SKUs
  const { data: productMetadata, isLoading: isProductMetadataLoading } = useProductMetaData({
    skusToFetch: skus || [],
    retailerId: Number(retailerId),
    enabled: !isProductEntity && skus?.length > 0
  });

  const newConversionRows = useMemo(() => {
    if (trafficDorisLoading || salesDorisLoading || isProductMetadataLoading) {
      return [];
    }

    const trafficData = trafficDorisResponse.primaryData?.records || [];
    const trafficComparisonData = trafficDorisResponse.comparisonData?.records || [];

    const salesData = salesDorisResponse?.primaryData?.records || [];
    const salesComparisonData = salesDorisResponse?.comparisonData?.records || [];

    // Create comparison data maps for easy lookup
    const salesComparisonMap = salesComparisonData.reduce((acc: any, row) => {
      if (isProductEntity) {
        acc[row.weekId] = row;
      } else {
        acc[row.fieldKey] = row;
      }
      return acc;
    }, {});

    const trafficComparisonMap = trafficComparisonData.reduce((acc: any, row) => {
      if (isProductEntity) {
        acc[row.weekId] = row;
      } else {
        acc[row.fieldKey] = row;
      }
      return acc;
    }, {});

    const rows = salesData.map((row) => {
      const trafficRow = trafficData.find((t) => {
        if (isProductEntity) {
          return t.weekId === row.weekId;
        } else {
          return t.fieldKey === row.fieldKey;
        }
      });

      const salesComparisonRow = salesComparisonMap[isProductEntity ? row.weekId : row.fieldKey];
      const trafficComparisonRow = trafficComparisonMap[isProductEntity ? row.weekId : row.fieldKey];

      const stacklineSku = row.fieldKey;
      const productMeta = productMetadata?.[stacklineSku];

      const salesRow = row as DorisSalesDataRecord;
      const salesComparisonRowTyped = salesComparisonRow as DorisSalesDataRecord;

      // Extract current period values
      const retailSales = salesRow.retailSales || 0;
      const unitsSold = salesRow.unitsSold || 0;
      const retailPrice = salesRow.retailPrice || 0;
      const totalClicks = trafficRow?.totalClicks || 0;

      // Extract comparison period values
      const retailSalesComparison = salesComparisonRowTyped?.retailSales || 0;
      const unitsSoldComparison = salesComparisonRowTyped?.unitsSold || 0;
      const retailPriceComparison = salesComparisonRowTyped?.retailPrice || 0;
      const totalClicksComparison = trafficComparisonRow?.totalClicks || 0;

      // Calculate purchase rates
      const purchaseRate = safeDivide(unitsSold, totalClicks);
      const purchaseRateComparison = safeDivide(unitsSoldComparison, totalClicksComparison);

      return {
        product: {
          ...productMeta
        },
        retailSales: {
          value: retailSales,
          difference: retailSales - retailSalesComparison
        },
        unitsSold: {
          value: unitsSold,
          difference: unitsSold - unitsSoldComparison
        },
        retailPrice: {
          value: retailPrice,
          difference: retailPrice - retailPriceComparison
        },
        totalClicks: {
          value: totalClicks,
          difference: totalClicks - totalClicksComparison
        },
        purchaseRate: {
          value: purchaseRate,
          difference: purchaseRate - purchaseRateComparison
        },
        weekId: row.weekId
      };
    });

    return rows;
  }, [
    trafficDorisLoading,
    salesDorisLoading,
    isProductMetadataLoading,
    trafficDorisResponse.primaryData?.records,
    trafficDorisResponse.comparisonData?.records,
    salesDorisResponse?.primaryData?.records,
    salesDorisResponse?.comparisonData?.records,
    isProductEntity,
    productMetadata
  ]);

  /**
   * Updates the cached data when new data is fetched
   */
  useEffect(() => {
    if (newConversionRows.length > 0) {
      setDataByPageChunk((oldData) => ({
        ...oldData,
        [currentChunk]: newConversionRows
      }));
    }
  }, [newConversionRows, currentChunk]);

  /**
   * The full table data from all cached chunks
   */
  const fullTableData = useMemo(() => {
    return Object.keys(dataByPageChunk)
      .sort((a, b) => Number(a) - Number(b))
      .reduce((acc: any[], chunkNumber) => {
        return [...acc, ...dataByPageChunk[Number(chunkNumber)]];
      }, []);
  }, [dataByPageChunk]);

  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const currentPageData = fullTableData.slice(startIndex, endIndex);

  const handleNextPage = useCallback(() => {
    const adjustedPage = ((page - 1) % PAGES_PER_CHUNK) + 1;
    const startIndex = (adjustedPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;

    const currentChunkData = dataByPageChunk[currentChunk] || [];

    // If we don't have enough data in current chunk, don't proceed
    if (currentChunkData.slice(startIndex, endIndex).length < PAGE_SIZE && currentChunkData.length < FETCH_CHUNK_SIZE) {
      return;
    }

    setPage(page + 1);
  }, [page, dataByPageChunk, currentChunk]);

  const handlePreviousPage = useCallback(() => {
    setPage(Math.max(page - 1, 1));
  }, [page]);

  return (
    <Flex flexDirection="column">
      <EntityTableHeader
        title={`Conversion Rate by ${isProductEntity ? 'Week' : 'Product'}`}
        enableSwitchingLayouts
        defaultView={viewMode}
        handleChangeLayout={() => setViewMode(viewMode === 'table' ? 'tile' : 'table')}
        paddingTop="0px"
        iconsList={isProductEntity ? [IconsList.export, IconsList.table] : undefined}
        customExportModal={(props: { open: boolean; close: () => void }) => (
          <AtlasSalesExportModal open={props.open} onClose={props.close} metricType={DorisMetricTypeEnum.Conversion} />
        )}
      />
      <Flex flexDirection="column" gap={viewMode === 'table' ? 0 : 20}>
        {viewMode === 'table' ? (
          isProductEntity ? (
            <EntityTable
              skeletonRows={PAGE_SIZE}
              isLoading={salesDorisLoading || trafficDorisLoading}
              showPagination={false}
              shouldModifyColumnDefs={false}
              columnDefs={[
                getWeekColumnDefinition({
                  getTableColumnProps(defaults) {
                    return {
                      ...defaults,
                      style: {
                        ...defaults.style,
                        width: '235px !important'
                      }
                    };
                  }
                }),
                ...METRICS.map(([fieldName, indexName]) => {
                  const field = INDEX_FIELDS.getField(getAppName(), indexName, fieldName);

                  return {
                    headerName: field.displayName,
                    valueFormatter: (_, row: ProductGridDatum) => {
                      const metricValue = _get(row, [fieldName, 'value'], 0);
                      return <MetricCell metricType={field.metricType} value={metricValue} />;
                    }
                  };
                })
              ]}
              rowData={currentPageData}
            />
          ) : (
            <EntityTable
              skeletonRows={PAGE_SIZE}
              isLoading={salesDorisLoading || trafficDorisLoading}
              showPagination={false}
              shouldModifyColumnDefs={false}
              columnDefs={[
                {
                  headerName: 'Products',
                  valueFormatter: (_, row: ProductGridDatum) => (
                    <div style={{ width: '359px' }}>
                      <ProductCell
                        key={row.product.stacklineSku}
                        stacklineSku={row.product.stacklineSku}
                        title={row.product.title}
                      />
                    </div>
                  )
                },
                ...METRICS.map(([fieldName, indexName]) => {
                  const field = INDEX_FIELDS.getField(getAppName(), indexName, fieldName);
                  return {
                    headerName: field.displayName,
                    valueFormatter: (_, row: ProductGridDatum) => {
                      const metricValue = _get(row, [fieldName, 'value'], 0);
                      const comparisonValue = metricValue - _get(row, [fieldName, 'difference'], 0);
                      const percentChange = calculatePercentChange(metricValue, comparisonValue);

                      return (
                        <MetricCell metricType={field.metricType} value={metricValue} percentChange={percentChange} />
                      );
                    }
                  };
                })
              ]}
              rowData={currentPageData}
              appTableProps={{
                noDataPlaceholder: TableNoDataPlaceHolder,
                sx: {
                  'tr td:first-child': {
                    width: '424px',
                    paddingRight: '40px'
                  }
                }
              }}
            />
          )
        ) : (
          <ProductGrid
            data={currentPageData as ProductGridDatum[]}
            loading={salesDorisLoading || trafficDorisLoading || isProductMetadataLoading}
            metricType={METRICTYPE.PERCENT}
            fieldName="purchaseRate"
            page={1}
            noDataPlaceholder={
              <Box sx={{ display: 'flex', justifyContent: 'center', margin: '80px 0' }}>
                <NoDataPlaceHolder
                  title="Data currently unavailable"
                  description="There is currently no data matching your selected criteria. Please try another query to view insights."
                  flexProps={{
                    padding: 0
                  }}
                />
              </Box>
            }
          />
        )}
        {shouldHidePagination({ page, pageSize: PAGE_SIZE, rowCount: fullTableData.length }) ? null : (
          <Flex justifyContent="flex-end">
            <Pagination
              currentPage={page}
              shouldUseInfinitePagination
              onClickNext={handleNextPage}
              onClickPrevious={handlePreviousPage}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
